import apiClient from './apiClient';

export const syncPayroll = async ({ scope = 'all', dateFrom, dateTo } = {}) => {
  const params = { scope };
  if (scope === 'range') { params.date_from = dateFrom; params.date_to = dateTo; }
  const res = await apiClient.post('/payroll/sync', null, { params });
  return res.data;
};

export const exportPayslipsCsv = async ({ dateFrom, dateTo } = {}) => {
  const params = {};
  if (dateFrom) params.date_from = dateFrom;
  if (dateTo) params.date_to = dateTo;
  const res = await apiClient.get('/payroll/export/csv', { params, responseType: 'blob' });
  return res.data;
};

export const getSupervisorAttendance = async ({ dateFrom, dateTo } = {}) => {
  const params = {};
  if (dateFrom) params.date_from = dateFrom;
  if (dateTo) params.date_to = dateTo;
  const res = await apiClient.get('/payroll/supervisor-attendance', { params });
  return res.data;
};

export const setSupervisorAttendance = async ({ supervisorStaffId, workDate, dayPresent, nightPresent } = {}) => {
  const res = await apiClient.post('/payroll/supervisor-attendance', {
    supervisor_staff_id: supervisorStaffId,
    work_date: workDate,
    day_present: !!dayPresent,
    night_present: !!nightPresent,
  });
  return res.data;
};

export const getPayrollSyncStatus = async (jobId) => {
  const res = await apiClient.get('/payroll/sync/status', {
    params: jobId ? { job_id: jobId } : {},
  });
  return res.data;
};

export const getPayrollLastSynced = async () => {
  const res = await apiClient.get('/payroll/sync/last');
  return res.data;
};

export const getPayrollMatrix = async ({ dateFrom, dateTo } = {}) => {
  const res = await apiClient.get('/payroll/matrix', {
    params: { date_from: dateFrom, date_to: dateTo },
  });
  return res.data;
};

export const getPayrollStaff = async () => {
  const res = await apiClient.get('/payroll/staff');
  return res.data;
};

export const getHourlyStaffReport = async ({ date, module = 'all', operation = 'all' } = {}) => {
  const params = { module, operation };
  if (date) params.date = date;
  const res = await apiClient.get('/payroll/hourly-report', { params });
  return res.data;
};

export const getOperationRankings = async (params) => {
  const res = await apiClient.get('/payroll/operation-rankings', { params, timeout: 120000 });
  return res.data;
};

export const getHourlyTargets = async () => {
  const res = await apiClient.get('/payroll/hourly-targets');
  return res.data;
};

export const saveHourlyTargets = async (targets) => {
  const res = await apiClient.post('/payroll/hourly-targets', { targets });
  return res.data;
};

export const getPayslip = async ({ staffId, dateFrom, dateTo } = {}) => {
  const res = await apiClient.get('/payroll/payslip', {
    params: { staff_id: staffId, date_from: dateFrom, date_to: dateTo },
  });
  return res.data;
};

export const getPayslips = async ({ staffIds = [], dateFrom, dateTo, detail = false } = {}) => {
  const res = await apiClient.get('/payroll/payslips', {
    params: { staff_ids: staffIds.join(','), date_from: dateFrom, date_to: dateTo, detail },
  });
  return res.data;
};

export const getPayrollRates = async () => {
  const res = await apiClient.get('/payroll/rates');
  return res.data;
};

export const savePayrollRate = async ({ operationCode, rate, unitType = 'usn', targetQty } = {}) => {
  const body = { operation_code: operationCode, rate, unit_type: unitType };
  if (targetQty !== undefined && targetQty !== null) body.target_qty = targetQty;
  const res = await apiClient.post('/payroll/rates', body);
  return res.data;
};

// ---- Shifts & premium settings ----
export const getPayrollModules = async () => {
  const res = await apiClient.get('/payroll/modules');
  return res.data;
};

export const getShifts = async () => {
  const res = await apiClient.get('/payroll/shifts');
  return res.data;
};

export const saveShift = async (shift) => {
  const res = await apiClient.post('/payroll/shifts', shift);
  return res.data;
};

export const deleteShift = async (shiftId) => {
  const res = await apiClient.delete(`/payroll/shifts/${encodeURIComponent(shiftId)}`);
  return res.data;
};

export const getPayrollSettings = async () => {
  const res = await apiClient.get('/payroll/settings');
  return res.data;
};

export const savePayrollSettings = async ({ crossModulePremiumPct, contractUpliftPct, sundayPremiumPct } = {}) => {
  const body = {};
  if (crossModulePremiumPct !== undefined) body.cross_module_premium_pct = crossModulePremiumPct;
  if (contractUpliftPct !== undefined) body.contract_uplift_pct = contractUpliftPct;
  if (sundayPremiumPct !== undefined) body.sunday_premium_pct = sundayPremiumPct;
  const res = await apiClient.post('/payroll/settings', body);
  return res.data;
};

export const getStaffModules = async ({ dateFrom, dateTo } = {}) => {
  const res = await apiClient.get('/payroll/staff-modules', {
    params: { date_from: dateFrom, date_to: dateTo },
  });
  return res.data;
};

export const setStaffModule = async ({ staffId, homeModule } = {}) => {
  const res = await apiClient.post('/payroll/staff-modules', {
    staff_id: staffId, home_module: homeModule || null,
  });
  return res.data;
};

export const setWorkerType = async ({ staffId, workerType } = {}) => {
  const res = await apiClient.post('/payroll/worker-types', {
    staff_id: staffId, worker_type: workerType || 'REGULAR',
  });
  return res.data;
};

// ---- Supervisors: per-operation supervisor wage + incentive ----
export const getSupervisors = async () => {
  const res = await apiClient.get('/payroll/supervisors');
  return res.data;
};

export const saveSupervisor = async ({ operationCode, supervisors } = {}) => {
  const res = await apiClient.post('/payroll/supervisors', {
    operation_code: operationCode,
    supervisors: (supervisors || []).map((s) => ({
      supervisor_staff_id: (s.staffId || '').trim() || null,
      rate: s.rate ?? 0,
      daily_incentive: s.dailyIncentive ?? 0,
    })),
  });
  return res.data;
};

export const getSupervisorCriteria = async () => {
  const res = await apiClient.get('/payroll/supervisor-criteria');
  return res.data;
};

export const saveSupervisorCriterion = async (criterion) => {
  const res = await apiClient.post('/payroll/supervisor-criteria', criterion);
  return res.data;
};

export const deleteSupervisorCriterion = async (criterionId) => {
  const res = await apiClient.delete(`/payroll/supervisor-criteria/${encodeURIComponent(criterionId)}`);
  return res.data;
};

export const getSupervisorsPayslip = async ({ dateFrom, dateTo } = {}) => {
  const res = await apiClient.get('/payroll/supervisors/payslip', {
    params: { date_from: dateFrom, date_to: dateTo },
  });
  return res.data;
};
